import React, { useCallback, useEffect, useState } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTranslation } from '@/hooks/useTranslation';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import NavigationBarButtonContainer from '@/components/Navigation/NavigationBarButtonContainer';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import DefaultRouteNavigationHeaderLeft from '@/components/Navigation/Routes/DefaultRouteNavigationHeaderLeft';
import Text from '@/components/Typography/Text';
import { topicApiSsr } from '@/lib/topicApi';
import useTopicPageFeedback from '@/hooks/useTopicPageFeedback';
import {
  getTopicNavName,
  setTopicNavEntry,
  titleFromTopicSlug,
} from '@/lib/topicNavCache';

function topicSlugFromParams(params) {
  const raw = params?.slug;
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw) && raw[0]) return String(raw[0]);
  return '';
}

const TopicStartNavigationHeaderTitle = React.memo(() => {
  const params = useLocalSearchParams();
  const slug = topicSlugFromParams(params);
  const slugNorm = slug ? String(slug).toLowerCase() : '';
  const { colors } = useTheme();
  const [title, setTitle] = useState(() => titleFromTopicSlug(slug));

  // Reset immediately when the route slug changes so we never flash the prior topic.
  useEffect(() => {
    setTitle(getTopicNavName(slug) || titleFromTopicSlug(slug));
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    if (!slug) return undefined;

    (async () => {
      try {
        const topic = await topicApiSsr.getBySlug(slug);
        if (cancelled || !topic?.name) return;
        if (String(topic.slug || '').toLowerCase() !== slugNorm) return;
        setTopicNavEntry(topic.slug, topic.name);
        setTitle(topic.name);
      } catch {
        /* keep slug-derived title */
      }
    })();

    return () => { cancelled = true; };
  }, [slug, slugNorm]);

  return (
    <Text
      numberOfLines={1}
      style={{ color: colors.text_color_primary, fontSize: 18, fontWeight: '400' }}
    >
      {title}
    </Text>
  );
});

TopicStartNavigationHeaderTitle.displayName = 'TopicStartNavigationHeaderTitle';

const TopicStartNavigationHeaderLeft = React.memo(() => {
  const router = useRouter();
  const { t } = useTranslation();

  const onBack = useCallback(() => {
    if (router.canGoBack?.()) {
      router.back();
      return;
    }
    router.push('/topics');
  }, [router]);

  return (
    <DefaultRouteNavigationHeaderLeft>
      <NavigationBarButton
        icon="chevron-left"
        tooltip={t('nav.routes.topics.backToTopicsTitle')}
        accessibilityLabel={t('nav.routes.topics.backToTopics')}
        onPress={onBack}
      />
    </DefaultRouteNavigationHeaderLeft>
  );
});

TopicStartNavigationHeaderLeft.displayName = 'TopicStartNavigationHeaderLeft';

const TopicStartNavigationHeaderRight = React.memo(() => {
  const params = useLocalSearchParams();
  const slug = topicSlugFromParams(params);
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const [topicName, setTopicName] = useState(() => getTopicNavName(slug) || titleFromTopicSlug(slug));
  const openTopicFeedback = useTopicPageFeedback(slug, topicName);

  useEffect(() => {
    setTopicName(getTopicNavName(slug) || titleFromTopicSlug(slug));
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    if (!slug) return undefined;

    const slugNorm = String(slug).toLowerCase();
    (async () => {
      try {
        const topic = await topicApiSsr.getBySlug(slug);
        if (cancelled || !topic?.name) return;
        if (String(topic.slug || '').toLowerCase() !== slugNorm) return;
        setTopicNavEntry(topic.slug, topic.name);
        setTopicName(topic.name);
      } catch {
        /* keep slug-derived title */
      }
    })();

    return () => { cancelled = true; };
  }, [slug]);

  return (
    <NavigationBarButtonContainer rightPadding>
      <NavigationBarButton
        icon="comment-dots"
        tooltip={t('nav.routes.topics.feedbackTitle')}
        accessibilityLabel={t('pages.topics.feedbackButton')}
        label={isXS ? undefined : t('pages.topics.feedbackButton')}
        onPress={openTopicFeedback}
      />
    </NavigationBarButtonContainer>
  );
});

TopicStartNavigationHeaderRight.displayName = 'TopicStartNavigationHeaderRight';

/**
 * /start/:slug — public topic SEO page; header shows the topic name.
 */
export function getTopicStartRouteNavigationOptions() {
  return {
    headerTitle: () => <TopicStartNavigationHeaderTitle />,
    headerBackVisible: false,
    headerShown: true,
    headerLeft: () => <TopicStartNavigationHeaderLeft />,
    headerRight: () => <TopicStartNavigationHeaderRight />,
  };
}

export default getTopicStartRouteNavigationOptions;
